import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  CardImg,
  Badge,
  Spinner,
  Input,
  Alert,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CampaignService from "../../../services/OrderingServices/CampaignService";
import CampaignAssignService from "../../../services/OrderingServices/CampaignAssignService";
import Select from "react-select";

// ----- Reusable Hook for Drag-and-Drop Reorder -----
const useReorderList = (initialList = []) => {
  const [items, setItems] = useState(initialList);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (index, e) => {
    setDraggedIndex(index);
    if (e.target) e.target.style.opacity = 0.5;
  };

  const handleDragEnd = (e) => {
    if (e.target) e.target.style.opacity = 1;
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (index, e) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (dropIndex, e) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(dropIndex, 0, draggedItem);

    setItems(newItems);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return {
    items,
    setItems,
    draggedIndex,
    dragOverIndex,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
  };
};

// ----- Main Component -----
const UnassignMedia = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignAds, setCampaignAds] = useState([]);
  const [adsSearch, setAdsSearch] = useState("");
  const [showReorderMode, setShowReorderMode] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [loading, setLoading] = useState(true);

  const CDN_BASE_URL = window.APP_CONFIG?.api3?.baseapi || "";

  // Reorder hook
  const {
    items: selectedAds,
    setItems: setSelectedAds,
    draggedIndex,
    dragOverIndex,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
  } = useReorderList([]);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  async function fetchCampaigns() {
    try {
      setLoading(true);
      const response = await CampaignService.campaignsList({ pageSize: 100 });
      setCampaigns(response?.data || []);
    } catch (error) {
      toast.error("Failed to load campaigns");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  // Fetch campaign details and get its assigned ads
  const handleCampaignSelect = async (option) => {
    try {
      setSelectedCampaign(option ? option.value : null);
      setSelectedAds([]);
      setAdsSearch("");
      setShowReorderMode(false);

      // If a campaign is selected, fetch its details to get assigned ads
      if (option && option.value) {
        const campaignDetails = await CampaignService.getCampaignById(option.value);
        
        // Set the ads assigned to this campaign
        if (campaignDetails.ads && campaignDetails.ads.length > 0) {
          setCampaignAds(campaignDetails.ads);
        } else {
          setCampaignAds([]);
          toast.info("This campaign has no assigned media");
        }
      } else {
        setCampaignAds([]);
      }
    } catch (error) {
      toast.error("Failed to fetch campaign details");
      console.error(error);
      setSelectedCampaign(null);
      setCampaignAds([]);
    }
  };

  const campaignOptions = campaigns.map((campaign) => ({
    value: campaign.id,
    label: campaign.description
      ? `${campaign.name} - ${campaign.description}`
      : campaign.name,
  }));

  const getMediaUrl = (ad) =>
    ad.mediaType === "Video" && ad.thumbnailFile
      ? `${CDN_BASE_URL}${ad.thumbnailFile}`
      : `${CDN_BASE_URL}${ad.mediaPath}`;

  const toggleAdSelection = (ad) => {
    const index = selectedAds.findIndex((a) => a.id === ad.id);
    if (index > -1) {
      setSelectedAds(selectedAds.filter((a) => a.id !== ad.id));
    } else {
      setSelectedAds([...selectedAds, ad]);
    }
  };

  const getAdPosition = (adId) => {
    const index = selectedAds.findIndex((a) => a.id === adId);
    return index > -1 ? index + 1 : null;
  };

  const handleUnassign = async () => {
    if (!selectedCampaign || selectedAds.length === 0) {
      toast.error("Please select a campaign and at least one media to unassign");
      return;
    }
    try {
      setUnassigning(true);
      await CampaignAssignService.unAssignAdsToCampaigns({
        campaignIds: [selectedCampaign],
        adIds: selectedAds.map((ad) => ad.id),
      });
      toast.success(
        `Successfully unassigned ${selectedAds.length} media from campaign!`
      );
      setSelectedCampaign(null);
      setCampaignAds([]);
      setSelectedAds([]);
      setShowReorderMode(false);
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to unassign media");
      console.error(error);
    } finally {
      setUnassigning(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedAds([]);
    setShowReorderMode(false);
  };

  const filteredAds = campaignAds.filter((a) =>
    a.title?.toLowerCase().includes(adsSearch.toLowerCase())
  );

  const displayAds = showReorderMode ? selectedAds : filteredAds;

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid className="text-center py-5">
          <Triangle color="#2c3e50" height={80} width={80} />
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Unassign Media from Campaign" pageTitle="Ads Management" />

        {/* Campaign Selection */}
        <Row className="">
          <Col lg="12">
            <Card className="border">
              <CardBody className="p-4">
                <h6 className="fw-bold mb-3">Select Campaign</h6>
                <Row className="align-items-end justify-content-between">
                  <Col md="3">
                    <Select
                      options={campaignOptions}
                      value={campaignOptions.find(
                        (option) => option.value === selectedCampaign
                      )}
                      onChange={handleCampaignSelect}
                      placeholder="Choose a campaign..."
                      isClearable
                    />
                  </Col>
                  <Col md="4" className="text-end">
                    {selectedCampaign && (
                      <Badge color="success" pill className="px-3 py-2">
                        <i className="ri-checkbox-circle-fill me-1" />
                        Campaign Selected
                      </Badge>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Show Campaign's Assigned Media Count */}
        {selectedCampaign && (
          <Row className="mb-4">
            <Col lg="12">
              <Card className="border-primary bg-light-primary">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center gap-2">
                    <i className="ri-information-line fs-5 text-primary" />
                    <div>
                      <h6 className="mb-0 fw-bold text-primary">
                        Campaign Assigned Media
                      </h6>
                      <small className="text-muted">
                        This campaign has {campaignAds.length} assigned media
                      </small>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Media Selection Controls */}
        {selectedCampaign && campaignAds.length > 0 && (
          <Row className="mb-4">
            <Col lg="12">
              <Alert color="warning" className="d-flex align-items-center mb-0">
                <i className="ri-alert-line fs-4 me-2" />
                <div className="flex-grow-1">
                  <strong>{selectedAds.length} media selected for unassignment</strong>
                  {selectedAds.length > 0 && (
                    <span className="text-muted ms-2">
                      • Click cards to select/deselect
                    </span>
                  )}
                </div>
                <div className="d-flex gap-2">
                  {selectedAds.length > 0 && (
                    <>
                      <Button
                        color="danger"
                        outline
                        size="sm"
                        onClick={handleClearSelection}
                      >
                        <i className="ri-close-circle-line me-1" />
                        Clear
                      </Button>
                    </>
                  )}
                  <Button
                    color="danger"
                    size="sm"
                    onClick={handleUnassign}
                    disabled={unassigning || selectedAds.length === 0}
                  >
                    {unassigning ? (
                      <>
                        <Spinner size="sm" className="me-1" />
                        Unassigning...
                      </>
                    ) : (
                      <>
                        <i className="ri-delete-bin-line me-1" />
                        Unassign ({selectedAds.length})
                      </>
                    )}
                  </Button>
                </div>
              </Alert>
            </Col>
          </Row>
        )}

        {/* Search */}
        {selectedCampaign && campaignAds.length > 0 && !showReorderMode && (
          <Row className="mb-4">
            <Col lg="12">
              <div className="position-relative">
                <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <Input
                  type="text"
                  placeholder="Search media by title..."
                  value={adsSearch}
                  onChange={(e) => setAdsSearch(e.target.value)}
                  className="ps-5"
                />
              </div>
            </Col>
          </Row>
        )}

        {/* Media Grid - Only Shows Campaign's Assigned Media */}
        {selectedCampaign && (
          <Row className="g-3">
            {campaignAds.length === 0 ? (
              <Col lg="12">
                <Card className="border">
                  <CardBody className="text-center py-5">
                    <i className="ri-inbox-line display-1 text-muted" />
                    <h5 className="mt-3 text-muted">No Assigned Media</h5>
                    <p className="text-muted small">
                      This campaign doesn't have any assigned media to unassign
                    </p>
                  </CardBody>
                </Card>
              </Col>
            ) : displayAds.length === 0 ? (
              <Col lg="12">
                <Card className="border">
                  <CardBody className="text-center py-5">
                    <i className="ri-search-2-line display-1 text-muted" />
                    <h5 className="mt-3 text-muted">No Media Found</h5>
                    <p className="text-muted small">
                      Try adjusting your search
                    </p>
                  </CardBody>
                </Card>
              </Col>
            ) : (
              displayAds.map((ad, index) => {
                const position = getAdPosition(ad.id);
                const isSelected = position !== null;
                const isDragging = draggedIndex === index;
                const isDragOver = dragOverIndex === index;

                return (
                  <Col key={ad.id} xs="12" sm="6" md="4" lg="3" xl="2" className="d-flex">
                    <Card
                      className={`w-100 border ${
                        isSelected ? "border-danger shadow-sm" : ""
                      } ${isDragOver ? "border-info bg-light" : ""}`}
                      onClick={() => toggleAdSelection(ad)}
                      style={{
                        cursor: "pointer",
                        opacity: isDragging ? 0.5 : 1,
                      }}
                    >
                      <div className="position-relative">
                        <CardImg
                          top
                          src={getMediaUrl(ad)}
                          alt={ad.title}
                          style={{ height: "180px", objectFit: "cover" }}
                          onError={(e) => {
                            e.target.src =
                              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f6f8fa'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23959da5' font-family='sans-serif' font-size='14'%3ENo Image%3C/text%3E%3C/svg%3E";
                          }}
                        />

                        {isSelected && (
                          <div className="position-absolute top-0 end-0 m-2">
                            <Badge color="danger" pill className="px-2 py-1">
                              <i className="ri-check-line me-1" />
                              Selected
                            </Badge>
                          </div>
                        )}

                        <div className="position-absolute start-0 top-0 m-2">
                          <Badge color="dark" className="text-white">
                            {ad.mediaType}
                          </Badge>
                        </div>

                        {isSelected && (
                          <div className="position-absolute bottom-0 start-0 end-0 m-2">
                            <Badge color="danger" className="w-100 text-center py-2">
                              <i className="ri-delete-bin-2-line me-1" />
                              Will be unassigned
                            </Badge>
                          </div>
                        )}
                      </div>

                      <CardBody className="p-3">
                        <h6 className="mb-2 text-truncate fw-semibold">{ad.title}</h6>
                        {ad.advertiserName && (
                          <div className="small text-muted mt-2">{ad.advertiserName}</div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                );
              })
            )}
          </Row>
        )}

        {/* Empty State - No Campaign Selected */}
        {!selectedCampaign && (
          <Row>
            <Col lg="12">
              <Card className="border">
                <CardBody className="text-center py-5">
                  <i className="ri-folder-3-line display-1 text-muted" />
                  <h4 className="mt-4 fw-bold">Select a Campaign to Begin</h4>
                  <p className="text-muted">
                    Choose a campaign from the dropdown above to view and unassign its assigned media
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default UnassignMedia;
